import React, { FC, useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { Box } from "@mui/material";
import { deepDerefrencer } from "../../../utils/form_factory";
import { Small } from "../../Typography";
import { ukoTheme } from "../../../theme";
import CircularProgress from "@mui/material/CircularProgress";

interface IFormMultiInputProps {
  name: string;
  label: any;
  formControl: any;
  isDisabled?: boolean;
  options?: string[];
  // Optional: when provided, suggestions are fetched live as the user types,
  // in addition to (or instead of) the static `options` list.
  dataFetcher?: (
    search: string,
    setData: (results: string[]) => void,
    extraFilters?: any,
  ) => Promise<any> | any;
  parseFilter?: (...params: any) => any;
  selector?: {
    value: (option: any) => any;
    label: (option: any) => any;
  };
}

const MultiInputField: FC<IFormMultiInputProps | any> = (
  props: IFormMultiInputProps,
) => {
  const theme = ukoTheme();
  const [inputValue, setInputValue] = useState<string>("");
  const [fetchedOptions, setFetchedOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const latestRequestId = useRef(0);

  // Normalize a string-or-object item to its comparison key / display label.
  const toValue = (item: any): any =>
    typeof item === "string" ? item : props.selector?.value(item) ?? item;

  const toLabel = (item: any): string =>
    typeof item === "string"
      ? item
      : props.selector?.label(item) ?? String(item);

  const currentValues = (): any[] =>
    deepDerefrencer(props.formControl.values, props.name) || [];

  const commitValue = (item: any) => {
    const normalized = toValue(item);
    const updatedValues = [...currentValues(), normalized];
    props.formControl.setFieldValue(props.name, updatedValues);
    setInputValue("");
    setFetchedOptions([]);
  };

  const handleInputChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: any,
  ) => {
    if (value === null) return;
    if (typeof value === "string") {
      if (value.trim() !== "") commitValue(value.trim());
      return;
    }
    commitValue(value);
  };

  const handleInputKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter" && inputValue.trim() !== "") {
      commitValue(inputValue.trim());
    }
  };

  const handleChipDelete = (item: any) => {
    const key = toValue(item);
    const updatedValues = currentValues().filter(
      (val: any) => toValue(val) !== key,
    );
    props.formControl.setFieldValue(props.name, updatedValues);
  };

  // Fetch suggestions as the user types, when a dataFetcher is supplied.
  useEffect(() => {
    if (!props.dataFetcher) {
      return;
    }

    const debounceSearch = debounce(async () => {
      const requestId = ++latestRequestId.current;
      const filters = props.parseFilter ? props.parseFilter() : {};

      setLoading(true);
      await props.dataFetcher!(
        inputValue,
        (results: string[]) => {
          // Ignore this response if a newer search has since been fired.
          if (requestId === latestRequestId.current) {
            setFetchedOptions(results || []);
          }
        },
        { ...filters },
      );
      if (requestId === latestRequestId.current) {
        setLoading(false);
      }
    }, 500);

    if (inputValue.trim()) {
      debounceSearch();
    } else {
      setFetchedOptions([]);
    }

    return () => {
      debounceSearch.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const selectedValues = currentValues();

  const selectedKeys = new Set(selectedValues.map(toValue));

  const suggestionPool = props.dataFetcher
    ? fetchedOptions
    : props.options || [];

  // De-dupe by key (not object reference), then drop anything already added.
  const poolByKey = new Map<any, any>();
  suggestionPool.forEach((item) => {
    const key = toValue(item);
    if (!poolByKey.has(key)) poolByKey.set(key, item);
  });
  const visibleOptions = Array.from(poolByKey.values()).filter(
    (item) => !selectedKeys.has(toValue(item)),
  );

  return (
    <Box>
      <Autocomplete
        freeSolo
        options={visibleOptions}
        value={inputValue}
        getOptionLabel={(option) => toLabel(option)}
        onChange={handleInputChange}
        loading={loading}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={props.label}
            variant="outlined"
            onKeyDown={handleInputKeyPress}
            disabled={props.isDisabled}
            sx={[styles.customStyles(theme), styles.inputStyles(theme)]}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, marginTop: 1 }}>
        {(props.formControl.values[props.name] || []).map(
          (value: string, index: number) => (
            <Chip
              key={index}
              label={toLabel(value)}
              onDelete={() => handleChipDelete(value)}
              sx={styles.chipStyles(theme)}
            />
          ),
        )}
      </Box>
      {Boolean(
        deepDerefrencer(props.formControl.touched, props.name) &&
          deepDerefrencer(props.formControl.errors, props.name),
      ) && (
        <Small
          color="error.main"
          fontSize="0.75rem"
          fontWeight={"normal"}
          marginTop={1}
        >
          {deepDerefrencer(props.formControl.errors, props.name)}
        </Small>
      )}
    </Box>
  );
};

const styles = {
  customStyles: (theme: any, outlineColor?: string) => ({
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "8px",
      border: "2px solid",
      borderColor: outlineColor
        ? outlineColor
        : theme.palette.mode === "light"
        ? theme.palette.secondary[300]
        : theme.palette.divider,
    },
  }),
  inputStyles: (theme: any) => ({
    width: "100%",
  }),
  chipStyles: (theme: any) => ({
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  }),
};

export default MultiInputField;
